// src/pages/offers/index.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

import {
  listOffers,
  getOffer,
  updateOfferStatus,
  listOfferHistory,
} from "../../utils/api-offers";

import NewOfferModal from "./components/NewOfferModal";
import OfferOCManagerModal from "./components/OfferOCManagerModal";
import OfferPdfPreviewModal from "../../components/offers/OfferPdfPreviewModal";
import OfferViewModal from "./components/OfferViewModal";
import OfferHistoryModal from "./components/OfferHistoryModal";
import ServicePricingModal from "./components/ServicePricingModal";
import ReadyToInvoiceModal from "./components/ReadyToInvoiceModal";
import { supabase } from "../../utils/supabase";

import {
  STATUS_OPTIONS,
  OfferActions,
  symbolFor,
  viaLabel,
  condTooltip,
} from "./offers.components";

import {
  OCMediaViewerModal,
  RemitosManagerModal,
  AcceptModal,
  RejectModal,
  RemitosSearchModal,
} from "./offers.modals";

// Debug temporal
if (typeof window !== "undefined") {
  window.supabase = supabase;
}

/* ========================= URL de video de fondo ========================= */
const VIDEO_URL =
  "https://qmrmkemkctikfoyxpanz.supabase.co/storage/v1/object/public/personalizacion/fondo%20panel2.mp4";

/* ========================= Iconos SVG (UI limpia) ========================= */
function IconChevron({ className = "" }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function IconEye({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPencil({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        d="M12 20h9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconFile({ className = "" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 2v6h6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ======================== Helper de Fecha ======================== */
const formatDateAR = (d) => {
  if (!d) return "";
  // Se asegura de limpiar la parte de hora si viene en formato ISO (T)
  const dateOnly = String(d).split("T")[0];
  const [y, m, day] = dateOnly.split("-");
  if (!y || !m || !day) return String(d);
  return `${day}/${m}/${y}`;
};

/* ======================== Month/Year Picker ======================== */
const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

function MonthYearPicker({ value, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // value = "YYYY-MM" o ""
  const selYear  = value ? value.slice(0, 4) : "";
  const selMonth = value ? value.slice(5, 7) : "";

  const handle = (newYear, newMonth) => {
    if (!newYear || !newMonth) { onChange(""); return; }
    onChange(`${newYear}-${newMonth}`);
  };

  return (
    <div className="flex items-center gap-1">
      <select
        value={selMonth}
        onChange={(e) => handle(selYear || String(currentYear), e.target.value)}
        className="field-lg text-sm py-1 px-2 rounded-md"
      >
        <option value="">Mes</option>
        {MESES.map((nombre, i) => {
          const val = String(i + 1).padStart(2, "0");
          return <option key={val} value={val}>{nombre}</option>;
        })}
      </select>
      <select
        value={selYear}
        onChange={(e) => handle(e.target.value, selMonth)}
        className="field-lg text-sm py-1 px-2 rounded-md"
      >
        <option value="">Año</option>
        {years.map((y) => (
          <option key={y} value={String(y)}>{y}</option>
        ))}
      </select>
    </div>
  );
}

async function attachInvoiceNumbersToOffers(offerRows = []) {
  const rows = Array.isArray(offerRows) ? offerRows : [];
  if (!rows.length) return rows;

  const offerIds = [...new Set(rows.map((row) => row?.id).filter(Boolean))];
  if (!offerIds.length) return rows;

  try {
    const { data: poRows, error: poErr } = await supabase
      .from("purchase_orders")
      .select("id,offer_id")
      .in("offer_id", offerIds);
    if (poErr) throw poErr;

    const poList = Array.isArray(poRows) ? poRows : [];
    const poByOfferId = new Map();
    poList.forEach((po) => {
      if (!po?.offer_id || !po?.id) return;
      if (!poByOfferId.has(po.offer_id)) poByOfferId.set(po.offer_id, []);
      poByOfferId.get(po.offer_id).push(po.id);
    });

    const poIds = [...new Set(poList.map((po) => po?.id).filter(Boolean))];
    if (!poIds.length) {
      return rows.map((row) => ({ ...row, invoice_numbers: [] }));
    }

    const { data: invoiceRows, error: invErr } = await supabase
      .from("billing_invoices")
      .select("po_id,invoice_number,requested_at,issued_at,created_at")
      .in("po_id", poIds)
      .order("created_at", { ascending: true });
    if (invErr) throw invErr;

    const invoicesByOfferId = new Map();
    const poToOfferId = new Map();
    poList.forEach((po) => {
      if (po?.id && po?.offer_id) poToOfferId.set(po.id, po.offer_id);
    });

    for (const inv of invoiceRows || []) {
      const offerId = poToOfferId.get(inv?.po_id);
      if (!offerId) continue;
      const raw = String(inv?.invoice_number || "").trim();
      if (!raw) continue;
      if (!invoicesByOfferId.has(offerId)) invoicesByOfferId.set(offerId, []);
      const current = invoicesByOfferId.get(offerId);
      if (!current.includes(raw)) current.push(raw);
    }

    return rows.map((row) => ({
      ...row,
      invoice_numbers: invoicesByOfferId.get(row.id) || [],
    }));
  } catch (e) {
    console.warn("[attachInvoiceNumbersToOffers]", e);
    return rows.map((row) => ({ ...row, invoice_numbers: [] }));
  }
}

/* ======================== Página principal ======================== */
export default function OffersPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [month, setMonth] = useState("");
  const [clientName, setClientName] = useState("");
  const [offers, setOffers] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const [openView, setOpenView] = useState(false);
  const [viewing, setViewing] = useState(null);

  const [openAccept, setOpenAccept] = useState(false);
  const [accepting, setAccepting] = useState(null);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfOffer, setPdfOffer] = useState(null);

  const [readyToInvoiceOpen, setReadyToInvoiceOpen] = useState(false);
  const [readyToInvoiceItems, setReadyToInvoiceItems] = useState([]);
  const [readyToInvoiceLoading, setReadyToInvoiceLoading] = useState(false);
  const [dismissedReadyToInvoiceIds, setDismissedReadyToInvoiceIds] = useState(
    () => {
      if (typeof window === "undefined") return [];
      try {
        const raw = window.localStorage.getItem(
          "offers.readyToInvoice.dismissed.v1"
        );
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch {
        return [];
      }
    }
  );
  const dismissedReadyToInvoiceIdsRef = useRef(dismissedReadyToInvoiceIds);

  // OC manager
  const [ocManagerOpen, setOcManagerOpen] = useState(false);
  const [ocManagerOffer, setOcManagerOffer] = useState(null);

  // Visor OC
  const [ocViewerOpen, setOcViewerOpen] = useState(false);
  const [ocViewerUrl, setOcViewerUrl] = useState("");

  // HISTORIAL
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyOffer, setHistoryOffer] = useState(null);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // VER SERVICIO (Precios)
  const [serviceOpen, setServiceOpen] = useState(false);
  const [selectedOfferId, setSelectedOfferId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  // RECHAZO
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejecting, setRejecting] = useState(null);

  // REMITOS
  const [remitosOpen, setRemitosOpen] = useState(false);
  const [remitosOffer, setRemitosOffer] = useState(null);

  // ✅ Estado nuevo para ver todos los remitos
  const [allRemitosOpen, setAllRemitosOpen] = useState(false);

  // ✅ NUEVO: fila expandida (acciones completas visibles)
  const [expandedOfferId, setExpandedOfferId] = useState(null);

  const openServiceLockRef = useRef(false);

  useEffect(() => {
    dismissedReadyToInvoiceIdsRef.current = dismissedReadyToInvoiceIds;
  }, [dismissedReadyToInvoiceIds]);

  const badgeForStatus = useMemo(
    () => ({
      draft: "badge-draft",
      sent: "badge-sent",
      accepted: "badge-accepted",
      billing:
        "bg-amber-600/25 text-amber-100 ring-1 ring-inset ring-amber-500/40",
      closed:
        "bg-zinc-600/25 text-zinc-100 ring-1 ring-inset ring-zinc-500/40",
      rejected:
        "bg-rose-600/20 text-rose-300 ring-1 ring-inset ring-rose-500/30",
    }),
    []
  );

  const loadReadyToInvoice = async () => {
    setReadyToInvoiceLoading(true);
    try {
      const cutoffIso = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data: remitoRows, error: remitoErr } = await supabase
        .from("remitos")
        .select("id, os_id, code, official_number, date_out, status, created_at, updated_at")
        .gte("created_at", cutoffIso)
        .order("created_at", { ascending: false });

      if (remitoErr) throw remitoErr;

      const remitos = Array.isArray(remitoRows) ? remitoRows : [];
      if (!remitos.length) {
        setReadyToInvoiceItems([]);
        return;
      }

      const osIds = [...new Set(remitos.map((row) => row?.os_id).filter(Boolean))];
      if (!osIds.length) {
        setReadyToInvoiceItems([]);
        return;
      }

      const { data: osRows, error: osErr } = await supabase
        .from("service_orders")
        .select("id, po_id, code, created_at, updated_at")
        .in("id", osIds);

      if (osErr) throw osErr;

      const serviceOrders = Array.isArray(osRows) ? osRows : [];
      const poIds = [...new Set(serviceOrders.map((row) => row?.po_id).filter(Boolean))];
      if (!poIds.length) {
        setReadyToInvoiceItems([]);
        return;
      }

      const { data: poRows, error: poErr } = await supabase
        .from("purchase_orders")
        .select("id,offer_id,code,base_number")
        .in("id", poIds);

      if (poErr) throw poErr;

      const purchaseOrders = Array.isArray(poRows) ? poRows : [];
      const offerIds = [...new Set(purchaseOrders.map((row) => row?.offer_id).filter(Boolean))];
      if (!offerIds.length) {
        setReadyToInvoiceItems([]);
        return;
      }

      const { data: billingOffers, error: offerErr } = await supabase
        .from("offers")
        .select("id,base_number,code,status,updated_at,client:clients!offers_client_id_fkey(id,name)")
        .in("id", offerIds);

      if (offerErr) throw offerErr;

      const offerRows = Array.isArray(billingOffers) ? billingOffers : [];

      const offersById = new Map();
      offerRows.forEach((offer) => {
        if (offer?.id && !offersById.has(offer.id)) offersById.set(offer.id, offer);
      });

      const osById = new Map();
      serviceOrders.forEach((os) => {
        if (os?.id && !osById.has(os.id)) osById.set(os.id, os);
      });

      const poById = new Map();
      purchaseOrders.forEach((po) => {
        if (po?.id && !poById.has(po.id)) poById.set(po.id, po);
      });

      const remitosByOsId = new Map();
      remitos.forEach((remito) => {
        const osId = remito?.os_id;
        if (!osId) return;
        const current = remitosByOsId.get(osId);
        const currentDate = current?.date_out || current?.created_at || current?.updated_at || "";
        const nextDate = remito?.date_out || remito?.created_at || remito?.updated_at || "";
        if (!current || String(nextDate) >= String(currentDate)) {
          remitosByOsId.set(osId, remito);
        }
      });

      const items = remitos
        .map((remito) => {
          const os = remito?.os_id ? osById.get(remito.os_id) || null : null;
          const po = os?.po_id ? poById.get(os.po_id) || null : null;
          const offer = po?.offer_id ? offersById.get(po.offer_id) || null : null;
          const latestRemito = os?.id ? remitosByOsId.get(os.id) || null : null;
          if (!os || !po || !offer || !latestRemito) return null;
          return { offer, os, po, remito: latestRemito };
        })
        .filter(Boolean);

      const visibleItems = items.filter((item) => {
        const isDismissed = dismissedReadyToInvoiceIdsRef.current.includes(
          item.offer?.id
        );
        return !isDismissed;
      });

      setReadyToInvoiceItems(visibleItems);
    } catch (e) {
      console.error("[readyToInvoice]", e);
      setReadyToInvoiceItems([]);
    } finally {
      setReadyToInvoiceLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data, count } = await listOffers({ q, status, month, clientName });
      const enriched = await attachInvoiceNumbersToOffers(data || []);
      setOffers(enriched);
      setCount(count);
    } catch (e) {
      console.error(e);
      alert("No se pudieron cargar las ofertas.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  useEffect(() => {
    loadReadyToInvoice();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("ready_to_invoice") === "1") {
      setReadyToInvoiceOpen(true);
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      loadReadyToInvoice();
    }, 20000);
    return () => clearInterval(timer);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        "offers.readyToInvoice.dismissed.v1",
        JSON.stringify(dismissedReadyToInvoiceIds)
      );
    } catch {
      // no-op
    }
  }, [dismissedReadyToInvoiceIds]);

  // ✅ Búsqueda en tiempo real con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      load();
    }, 400); // 400ms de debounce para una búsqueda fluida

    return () => clearTimeout(timer);
  }, [q, status, month, clientName]); // eslint-disable-line

  useEffect(() => {
    let cancel = false;
    async function loadUser() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!cancel) setCurrentUserId(user?.id || null);
    }
    loadUser();
    return () => {
      cancel = true;
    };
  }, []);

  const handleSearch = async () => load();

  const openViewWithData = async (row) => {
    try {
      const full = await getOffer(row.id);
      setViewing(full);
      setOpenView(true);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar la oferta.");
    }
  };

  const openEdit = async (row) => {
    try {
      const full = await getOffer(row.id);
      setEditing(full);
      setOpenNew(true);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar la oferta para editar.");
    }
  };

  const openAcceptFor = (row) => {
    setAccepting(row);
    setOpenAccept(true);
  };

  const openRejectFor = (row) => {
    setRejecting(row);
    setRejectOpen(true);
  };

  async function openPdfPreview(row) {
    try {
      const full = await getOffer(row.id);
      setPdfOffer(full);
      setPdfOpen(true);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar la oferta para el PDF.");
    }
  }

  const openOcManager = async (row) => {
    if (!row?.id) return;
    try {
      const full = await getOffer(row.id);
      setOcManagerOffer(full);
      setOcManagerOpen(true);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar la gestión de órdenes de compra.");
    }
  };

  const openOcViewer = (url) => {
    if (!url) return;
    setOcViewerUrl(url);
    setOcViewerOpen(true);
  };

  async function openHistory(row) {
    try {
      const full = await getOffer(row.id);
      setHistoryOffer(full);
      setHistoryOpen(true);
      setHistoryLoading(true);
      const items = await listOfferHistory(full.id);
      setHistoryItems(items);
    } catch (e) {
      console.error(e);
      alert("No se pudo cargar el historial.");
    } finally {
      setHistoryLoading(false);
    }
  }

  const openService = async (row) => {
    if (openServiceLockRef.current) return;
    openServiceLockRef.current = true;
    try {
      setSelectedOfferId(row.id);
      setServiceOpen(true);
    } catch (e) {
      console.error("[openService] error:", e);
      alert("No se pudo abrir 'Ver servicio'.");
    } finally {
      openServiceLockRef.current = false;
    }
  };

  const openRemitos = (row) => {
    setRemitosOffer(row);
    setRemitosOpen(true);
  };

  const refreshAfterStatusChange = async () => {
    await load();
    await loadReadyToInvoice();
  };

  const handleMarkReadyToInvoiceAsFacturado = (offerId) => {
    if (!offerId) return;
    setDismissedReadyToInvoiceIds((prev) =>
      prev.includes(offerId) ? prev : [...prev, offerId]
    );
    setReadyToInvoiceItems((prev) =>
      prev.filter((item) => item.offer?.id !== offerId)
    );
  };

  const toggleExpanded = (offerId) => {
    setExpandedOfferId((prev) => (prev === offerId ? null : offerId));
  };

  // Botón compacto (mismo look para todos)
  const CompactActionBtn = ({
    title,
    onClick,
    variant = "default",
    icon,
    label,
  }) => {
    const base =
      "h-9 inline-flex items-center gap-2 px-3 rounded-md border transition-colors select-none";
    const variants = {
      default:
        "border-border/40 text-foreground/90 hover:bg-surface-2/60",
      warn:
        "border-amber-500/40 text-amber-200 hover:bg-amber-500/10",
      danger:
        "border-rose-500/40 text-rose-200 hover:bg-rose-500/10",
    };

    return (
      <button
        type="button"
        className={`${base} ${variants[variant] || variants.default}`}
        onClick={onClick}
        title={title}
      >
        {icon}
        <span className="text-sm">{label}</span>
      </button>
    );
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Video de fondo */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="pointer-events-none select-none absolute inset-0 w-full h-full object-cover z-0"
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* Overlay para legibilidad */}
      <div className="absolute inset-0 z-[1] bg-black/35" />

      {/* Contenido */}
      <div className="relative z-10">
        {/* Título + Nueva oferta */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-foreground">Ofertas</h1>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className={`rounded-lg border px-4 py-2 text-sm font-semibold shadow-sm transition-colors ${
                  readyToInvoiceItems.length > 0
                    ? "border-amber-400/50 bg-amber-500/20 text-amber-50 hover:bg-amber-500/30"
                    : "border-emerald-400/40 bg-emerald-500/15 text-emerald-50 hover:bg-emerald-500/20"
                }`}
                onClick={() => setReadyToInvoiceOpen(true)}
                title={
                  readyToInvoiceItems.length > 0
                    ? "Ver ofertas listas para facturar"
                    : "No hay ofertas para facturar"
                }
                disabled={readyToInvoiceLoading}
              >
                {readyToInvoiceLoading
                  ? "Actualizando..."
                  : readyToInvoiceItems.length > 0
                    ? `${readyToInvoiceItems.length} oferta${
                        readyToInvoiceItems.length === 1 ? "" : "s"
                      } lista${
                        readyToInvoiceItems.length === 1 ? "" : "s"
                      } para facturar`
                    : "Sin ofertas para facturar"}
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary-lift"
                onClick={() => setOpenNew(true)}
              >
                + Nueva oferta
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Filtros */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
        >
          <div className="card shadow-industrial-md p-4 mb-4 bg-surface/70 backdrop-blur [--tw-backdrop-blur:blur(6px)]">
            {/* Fila 1: búsqueda + estado + botón */}
            <div className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 md:col-span-6">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="field-lg"
                  placeholder="Buscar por Nº (5594), código (CT-5594-00) o CUIT…"
                />
              </div>
              <div className="col-span-12 md:col-span-4">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="field-lg"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-12 md:col-span-2">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full px-4 py-2 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 transition-colors"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? "Buscando…" : "Buscar"}
                </motion.button>
              </div>
            </div>

            {/* Fila 2: mes + cliente + resultados + remitos */}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {/* Filtro mes */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted whitespace-nowrap">Mes:</label>
                <MonthYearPicker value={month} onChange={setMonth} />
                {month && (
                  <button type="button" onClick={() => setMonth("")}
                    className="text-xs text-muted hover:text-foreground transition-colors" title="Limpiar">
                    ✕
                  </button>
                )}
              </div>

              {/* Filtro cliente */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted whitespace-nowrap">Cliente:</label>
                <div className="relative">
                  <input
                    value={clientName}
                    onChange={(e) => setClientName(e.target.value)}
                    placeholder="Nombre del cliente…"
                    className="field-lg text-sm py-1 px-2 pr-6 rounded-md w-44"
                  />
                  {clientName && (
                    <button
                      type="button"
                      onClick={() => setClientName("")}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted hover:text-foreground transition-colors text-xs"
                      title="Limpiar"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Limpiar todos */}
              {(month || clientName) && (
                <button
                  type="button"
                  onClick={() => { setMonth(""); setClientName(""); }}
                  className="text-xs text-rose-400 hover:text-rose-300 transition-colors border border-rose-500/30 rounded-md px-2 py-1"
                >
                  Limpiar filtros
                </button>
              )}

              <div className="ml-auto flex items-center gap-3">
                <div className="text-xs text-muted">{count} resultados</div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-3 py-1.5 rounded-md border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-colors text-sm"
                  onClick={() => setAllRemitosOpen(true)}
                  title="Ver todos los remitos"
                >
                  Todos los Remitos
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabla */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="w-full max-w-[1600px] mx-auto px-2 sm:px-4 lg:px-6 pb-12"
        >
          <div className="overflow-x-auto overflow-y-hidden rounded-xl border border-border/40 shadow-industrial-md bg-surface/80 backdrop-blur [--tw-backdrop-blur:blur(6px)]">
            <table className="table-modern min-w-[1100px]">
              <thead className="bg-surface/50 text-muted">
                <tr>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Nº</th>
                  <th className="text-left px-4 py-3 whitespace-nowrap">
                    Código
                  </th>
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="hidden xl:table-cell text-left px-4 py-3">
                    CUIT
                  </th>

                  <th className="text-right px-4 py-3 whitespace-nowrap">
                    Total Neto
                  </th>

                  <th className="text-left px-4 py-3">Estado</th>
                  <th className="hidden xl:table-cell text-left px-4 py-3 whitespace-nowrap">
                    Cond. Com.
                  </th>

                  <th className="text-left px-4 py-3 whitespace-nowrap">
                    OC
                  </th>

                  <th className="text-left px-4 py-3 min-w-[320px] sticky right-0 bg-surface/80 backdrop-blur border-l border-border/30">
                    Acciones
                  </th>
                </tr>
              </thead>

              <tbody>
                {offers.length === 0 && !loading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-muted">
                      No hay ofertas.
                    </td>
                  </tr>
                )}

                <AnimatePresence>
                  {offers.map((o, idx) => {
                    const prettyStatus =
                      o.status === "accepted" && o.accepted_via
                        ? `Aceptada (${viaLabel(o.accepted_via)})`
                        : STATUS_OPTIONS.find((s) => s.value === o.status)
                            ?.label || "—";
                    const ocDocs = Array.isArray(o.oc_documents) && o.oc_documents.length
                      ? o.oc_documents
                      : o.oc_pdf_url
                        ? [
                            {
                              id: "legacy-oc",
                              oc_number: o.oc_number || "",
                              file_url: o.oc_pdf_url,
                              file_name: "Orden de compra",
                            },
                          ]
                        : [];
                    const ocNumbers = ocDocs
                      .map((doc) => String(doc?.oc_number || "").trim())
                      .filter(Boolean);
                    const ocDocsCount = ocDocs.length;

                    const statusTitle =
                      o.status === "rejected" && o.rejection_reason
                        ? `Motivo: ${o.rejection_reason}`
                        : undefined;

                    const cuitValue = o.client?.cuit || o.client?.tax_id || "—";
                    const condValue = o.commercial_condition || "—";
                    const invoiceNumbers = [
                      ...(Array.isArray(o.invoice_numbers) ? o.invoice_numbers : []),
                      ...(o.invoice_number ? [String(o.invoice_number).trim()] : []),
                    ].filter(Boolean);
                    const uniqueInvoiceNumbers = [...new Set(invoiceNumbers)];
                    const primaryInvoiceNumber = uniqueInvoiceNumbers[0] || "";
                    const extraInvoiceNumbers = uniqueInvoiceNumbers.slice(1);
                    const isExpanded = expandedOfferId === o.id;
                    const subtotalBeforeDiscount = Number(o.subtotal || 0);
                    const discountValue = Math.max(0, Number(o.discount || 0));
                    const discountType = o.discount_type || "percentage";
                    const discountAmount =
                      discountValue > 0
                        ? discountType === "fixed"
                          ? Math.min(subtotalBeforeDiscount, discountValue)
                          : Math.min(
                              subtotalBeforeDiscount,
                              subtotalBeforeDiscount * (discountValue / 100)
                            )
                        : 0;
                    const hasDiscount = discountAmount > 0;
                    const netTotal = Math.max(0, subtotalBeforeDiscount - discountAmount);
                    const discountLabel =
                      discountType === "fixed"
                        ? "Descuento aplicado"
                        : `Descuento ${discountValue}%`;

                    return (
                      <motion.tr
                        key={o.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2, delay: idx * 0.03 }}
                        className={`row hover:bg-surface-2/60 transition-all duration-200 ${
                          isExpanded ? "bg-surface-2/30" : ""
                        }`}
                      >
                        <td className="px-4 py-3 text-foreground whitespace-nowrap">
                          {o.base_number ?? "—"}
                        </td>

                        <td className="px-4 py-3 whitespace-nowrap">
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.98 }}
                            className="text-primary hover:underline transition-all"
                            onClick={() => openViewWithData(o)}
                            title="Ver oferta"
                          >
                            {o.code ?? "—"}
                          </motion.button>
                        </td>

                        <td className="px-4 py-3 text-foreground">
                          <div className="font-medium">
                            {o.client?.name ?? "—"}
                          </div>

                          <div className="mt-1 text-xs text-muted xl:hidden space-y-0.5">
                            <div className="truncate">
                              <span className="opacity-80">CUIT:</span>{" "}
                              {cuitValue}
                            </div>
                            <div className="truncate">
                              <span className="opacity-80">Cond.:</span>{" "}
                              {condValue}
                            </div>
                          </div>
                        </td>

                        <td className="hidden xl:table-cell px-4 py-3 text-muted whitespace-nowrap">
                          {cuitValue}
                        </td>

                        <td
                          className={`px-4 py-3 text-right whitespace-nowrap ${
                            hasDiscount ? "text-orange-400" : "text-data"
                          }`}
                        >
                          <div className="font-semibold">
                            {symbolFor(o.price_currency)}
                            {netTotal.toFixed(2)}
                          </div>
                          {hasDiscount ? (
                            <div className="mt-1 text-[11px] leading-tight text-orange-300">
                              {discountLabel}
                            </div>
                          ) : null}
                          {primaryInvoiceNumber ? (
                            <div className="mt-1 text-[11px] leading-tight text-muted whitespace-normal max-w-[220px] ml-auto">
                              <div>
                                {uniqueInvoiceNumbers.length === 1 ? "Factura" : "Primera factura"}:{" "}
                                {primaryInvoiceNumber}
                              </div>
                              {extraInvoiceNumbers.length ? (
                                <div className="mt-0.5">
                                  Otras: {extraInvoiceNumbers.join(" · ")}
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </td>

                        {/* ✅ CAMBIO SOLICITADO: COLUMNA ESTADO */}
                        <td className="px-4 py-3" title={statusTitle}>
                          <div className="flex flex-col">
                            <span
                              className={`badge ${
                                badgeForStatus[o.status] || "badge-draft"
                              }`}
                            >
                              {prettyStatus}
                            </span>

                            {/* ✅ Cerrada: factura + fechas formateadas */}
                            {o.status === "closed" ? (
                              <>
                                {o.invoice_number ? (
                                  <span className="mt-1 text-[11px] text-muted">
                                    Factura: {o.invoice_number}
                                  </span>
                                ) : null}

                                {o.billing_requested_date ? (
                                  <span className="mt-1 text-[11px] text-muted">
                                    Pedido: {formatDateAR(o.billing_requested_date)}
                                  </span>
                                ) : null}

                                {o.invoiced_date ? (
                                  <span className="mt-1 text-[11px] text-muted">
                                    Emitida: {formatDateAR(o.invoiced_date)}
                                  </span>
                                ) : null}
                              </>
                            ) : null}

                            {(o.status === "accepted" ||
                              o.accepted_via === "oc" ||
                              ocNumbers.length ||
                              o.oc_number) ? (
                              <div className="mt-1 space-y-0.5 text-[11px] text-muted leading-tight">
                                {ocNumbers.slice(0, 2).length > 0 ? (
                                  ocNumbers.slice(0, 2).map((num, index) => (
                                    <span key={`${o.id}-oc-${index}`} className="block">
                                      OC {index + 1}: #{num}
                                    </span>
                                  ))
                                ) : o.oc_number ? (
                                  <span className="block">OC: #{o.oc_number}</span>
                                ) : (
                                  <span className="block">OC: pendiente de recepción</span>
                                )}
                                {ocNumbers.length > 2 ? (
                                  <span className="block">
                                    + {ocNumbers.length - 2} más
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </td>

                        <td className="hidden xl:table-cell px-4 py-3 text-foreground whitespace-nowrap">
                          {o.commercial_condition ? (
                            <div className="flex flex-col">
                            <span
                              title={condTooltip(o)}
                              className="whitespace-nowrap"
                            >
                              {o.commercial_condition}
                            </span>
                            <span className="text-[11px] text-zinc-500 mt-0.5">
                              {formatDateAR(o.created_at)}
                            </span>
                            </div>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3 whitespace-nowrap">
                          {ocDocs[0]?.file_url ? (
                            <button
                              onClick={() => openOcViewer(ocDocs[0].file_url)}
                              className="text-warn hover:underline"
                              title={ocDocs[0].file_name || "Ver OC"}
                            >
                              Ver OC{ocDocsCount > 1 ? ` (${ocDocsCount})` : ""}
                            </button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>

                        {/* ✅ Acciones sticky (UI mejorada) */}
                        <td className="px-3 py-2 sticky right-0 bg-surface/80 backdrop-blur border-l border-border/30">
                          {/* Barra compacta */}
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {/* Chevron */}
                              <button
                                type="button"
                                onClick={() => toggleExpanded(o.id)}
                                className={`h-9 w-9 inline-flex items-center justify-center rounded-md border transition-colors ${
                                  isExpanded
                                    ? "border-primary/40 text-primary bg-primary/10"
                                    : "border-border/40 text-foreground/80 hover:bg-surface-2/60"
                                }`}
                                title={
                                  isExpanded
                                    ? "Ocultar acciones"
                                    : "Mostrar acciones"
                                }
                              >
                                <span
                                  className={`inline-flex transition-transform duration-200 ${
                                    isExpanded ? "rotate-180" : "rotate-0"
                                  }`}
                                >
                                  <IconChevron className="h-5 w-5" />
                                </span>
                              </button>

                              {/* Acciones mínimas */}
                              {!isExpanded && (
                                <div className="flex items-center gap-2">
                                  <CompactActionBtn
                                    title="Ver"
                                    onClick={() => openViewWithData(o)}
                                    variant="default"
                                    icon={<IconEye className="h-4 w-4" />}
                                    label="Ver"
                                  />

                                  <CompactActionBtn
                                    title="Editar"
                                    onClick={() => openEdit(o)}
                                    variant="warn"
                                    icon={<IconPencil className="h-4 w-4" />}
                                    label="Editar"
                                  />

                                  <CompactActionBtn
                                    title="PDF"
                                    onClick={() => openPdfPreview(o)}
                                    variant="danger"
                                    icon={<IconFile className="h-4 w-4" />}
                                    label="PDF"
                                  />
                                </div>
                              )}
                            </div>

                            {!isExpanded && (
                              <span className="text-xs text-muted whitespace-nowrap">
                                Acciones…
                              </span>
                            )}
                          </div>

                          {/* Desplegable completo */}
                          <AnimatePresence initial={false}>
                            {isExpanded && (
                              <motion.div
                                key={`actions-${o.id}`}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.18 }}
                                className="mt-2 pt-2 border-t border-border/30"
                              >
                                <OfferActions
                                  offer={o}
                                  onView={openViewWithData}
                                  onEdit={openEdit}
                                  onPdf={openPdfPreview}
                                  onAccept={openAcceptFor}
                                  onMarkSent={(offer) => {
                                    if (offer.status === "accepted") {
                                      const ok = window.confirm(
                                        "Esta acción revertirá la oferta de Aceptada a Enviada. ¿Continuar?"
                                      );
                                      if (!ok) return;
                                    }
                                    updateOfferStatus(offer.id, "sent").then(
                                      refreshAfterStatusChange
                                    );
                                  }}
                                  onMarkBilling={(offer) => {
                                    openService(offer);
                                  }}
                                  onMarkClosed={async (offer) => {
                                    try {
                                      await updateOfferStatus(offer.id, "closed");

                                      setOffers((prev) =>
                                        prev.map((o) =>
                                          o.id === offer.id
                                            ? { ...o, status: "closed" }
                                            : o
                                        )
                                      );

                                      await refreshAfterStatusChange();
                                    } catch (e) {
                                      console.error(e);
                                      alert("No se pudo marcar como cerrada.");
                                    }
                                  }}
                                  onReopen={async (offer) => {
                                    if (
                                      !window.confirm(
                                        `¿Reabrir la oferta ${offer.code || offer.id}?\n\nVolverá al estado "Facturando" y podrá registrar nuevas facturas.`
                                      )
                                    )
                                      return;
                                    try {
                                      await updateOfferStatus(offer.id, "billing");
                                      setOffers((prev) =>
                                        prev.map((o) =>
                                          o.id === offer.id
                                            ? { ...o, status: "billing" }
                                            : o
                                        )
                                      );
                                      await refreshAfterStatusChange();
                                    } catch (e) {
                                      console.error(e);
                                      alert("No se pudo reabrir la oferta.");
                                    }
                                  }}
                                  onUploadOC={(offer) => openOcManager(offer)}
                                  onRemitos={openRemitos}
                                  onHistory={openHistory}
                                  onViewService={openService}
                                  onReject={openRejectFor}
                                />
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </td>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>

                {loading && (
                  <tr>
                    <td colSpan={9} className="px-4 py-6 text-center text-muted">
                      Cargando…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Modales */}
      <AnimatePresence>
        {openNew && (
          <NewOfferModal
            isOpen={openNew}
            onClose={() => {
              setOpenNew(false);
              setEditing(null);
            }}
            load={load}
            offer={editing || null}
          />
        )}
      </AnimatePresence>

      <OfferViewModal
        open={openView}
        offer={viewing}
        onClose={() => {
          setOpenView(false);
          setViewing(null);
        }}
      />

      <AcceptModal
        open={openAccept}
        offer={accepting}
        onClose={() => setOpenAccept(false)}
        onAccepted={() => load()}
      />

      <RejectModal
        open={rejectOpen}
        offer={rejecting}
        currentUserId={currentUserId}
        onClose={() => {
          setRejectOpen(false);
          setRejecting(null);
        }}
        onRejected={() => load()}
      />

      <AnimatePresence>
        {pdfOpen && (
          <OfferPdfPreviewModal
            isOpen={pdfOpen}
            offer={pdfOffer}
            onClose={() => {
              setPdfOpen(false);
              setPdfOffer(null);
            }}
            onSaved={() => {
              setPdfOpen(false);
              setPdfOffer(null);
              load();
            }}
          />
        )}
      </AnimatePresence>

      <OCMediaViewerModal
        open={ocViewerOpen}
        url={ocViewerUrl}
        onClose={() => {
          setOcViewerOpen(false);
          setOcViewerUrl("");
        }}
      />

      <OfferOCManagerModal
        open={ocManagerOpen}
        offer={ocManagerOffer}
        onClose={() => {
          setOcManagerOpen(false);
          setOcManagerOffer(null);
        }}
        onSaved={() => load()}
      />

      <AnimatePresence>
        {historyOpen && (
          <OfferHistoryModal
            open={historyOpen}
            offer={historyOffer}
            items={historyItems}
            loading={historyLoading}
            onClose={() => {
              setHistoryOpen(false);
              setHistoryOffer(null);
              setHistoryItems([]);
            }}
          />
        )}
      </AnimatePresence>

      {serviceOpen && (
        <ServicePricingModal
          offerId={selectedOfferId}
          open={serviceOpen}
          onClose={() => {
            setServiceOpen(false);
            setSelectedOfferId(null);
          }}
          onUpdated={load}
          currentUserId={currentUserId}
        />
      )}

      <RemitosManagerModal
        open={remitosOpen}
        offer={remitosOffer}
        onClose={() => {
          setRemitosOpen(false);
          setRemitosOffer(null);
        }}
        onChanged={refreshAfterStatusChange}
      />

      <RemitosSearchModal
        open={allRemitosOpen}
        onClose={() => setAllRemitosOpen(false)}
      />

      <ReadyToInvoiceModal
        open={readyToInvoiceOpen}
        offers={readyToInvoiceItems}
        onClose={() => setReadyToInvoiceOpen(false)}
        onOpenOffer={openViewWithData}
        onOpenService={openService}
        onMarkInvoiced={handleMarkReadyToInvoiceAsFacturado}
      />
    </div>
  );
}
